import {apiFetch} from '../helpers/WorkSheetContext';

/**
 * Client of the store-wide dashboard endpoints used by the workspace dashboard.
 *
 * Backend (HappyPOS-backend):
 *   - GET  /api/main-dashboard/summary          mainDashboard.controller `getMainDashboardSummary`
 *   - GET  /api/main-dashboard/modules          mainDashboard.controller `listMainDashboardModules`
 *   - POST /api/dashboardWidgetAi/generate      dashboardWidgetAi.controller `generateDashboardLayout`
 *
 * All three are the SAME endpoints the admin's global dashboard uses - the phone
 * adds no endpoint of its own. Store scope is sent as `x-restaurant-id` (validated
 * server-side against the caller's own stores) and mirrored in the query.
 */

/**
 * @param {?string} restaurantId Store id (`null` = the caller's active/primary store).
 * @returns {Record<string, string>} Headers to merge into the request.
 */
const scopeHeaders = (restaurantId) => (restaurantId ? {'x-restaurant-id': String(restaurantId)} : {});

/**
 * Fetch the module summary for one period / store.
 *
 * @param {object} params Request.
 * @param {string} params.period Period key (`today`, `7d`, `30d`, `mtd`, `lastMonth`, `qtd`, `ytd`, ...).
 * @param {?string[]} [params.modules] Module keys to compute (`null`/omitted = every accessible module).
 * @param {?string} [params.restaurantId] Store id.
 * @param {boolean} [params.force] Bypass the transport GET cache (pull-to-refresh).
 * @returns {Promise<object>} The summary payload.
 * @throws {Error} With `.status` on a non-2xx answer (403 no store scope, 400 bad period, ...).
 */
export const fetchMainDashboardSummary = async ({period, modules, restaurantId, force = false}) => {
    const query = [`period=${encodeURIComponent(period)}`];

    if (Array.isArray(modules) && modules.length) {
        query.push(`modules=${encodeURIComponent(modules.join(','))}`);
    }
    if (restaurantId) {
        query.push(`restaurantId=${encodeURIComponent(String(restaurantId))}`);
    }

    return apiFetch(`/main-dashboard/summary?${query.join('&')}`, {
        headers: scopeHeaders(restaurantId),
        ...(force ? {skipCache: true} : {}),
    });
};

/**
 * Which dashboard modules the caller may open (the backend's own access gate).
 *
 * @param {?string} [restaurantId] Store id.
 * @returns {Promise<{modules: Array<{key: string, moduleId: ?string, accessible: boolean}>}>} Module list.
 */
export const fetchMainDashboardModules = async (restaurantId) => apiFetch(
    `/main-dashboard/modules${restaurantId ? `?restaurantId=${encodeURIComponent(restaurantId)}` : ''}`,
    {headers: scopeHeaders(restaurantId)},
);

/**
 * Ask the AI to pick widgets for a phone dashboard from a free-text description.
 * `target: 'mobile'` makes the backend return a compact (<= 8) selection.
 *
 * @param {object} params Request.
 * @param {string} params.prompt What the user wants to see (<= 500 chars).
 * @param {Array<{id: string, title: string, description: string, category: string, kind: string}>} params.availableWidgets Allow-list.
 * @param {string} [params.locale] Locale of the rationale.
 * @param {?string} [params.restaurantId] Store id (AI credit is metered per store).
 * @returns {Promise<{widgetIds: string[], rationale: string, message?: string}>} Suggestion (possibly empty).
 * @throws {Error} With `.status` 403 when the store's AI credit is exhausted.
 */
export const generateWorkspaceLayout = async ({prompt, availableWidgets, locale, restaurantId}) => apiFetch('/dashboardWidgetAi/generate', {
    method: 'POST',
    headers: scopeHeaders(restaurantId),
    body: JSON.stringify({
        prompt,
        availableWidgets,
        dashboardKey: 'mobileWorkspaces',
        target: 'mobile',
        ...(locale ? {locale} : {}),
    }),
});
